import type {
  CreateTaskRequest,
  CreateTaskResponse,
  CreateTaskCommentRequest,
  CreateTaskCommentResponse,
  UpdateTaskCommentRequest,
  UpdateTaskCommentResponse,
  DeadlineVerificationRequest,
  DeadlineVerificationResponse,
  TaskAttachment,
  TaskAdditionalFieldItem,
  TaskAdditionalFieldCatalogItem,
  TaskDetails,
  TaskListFilters,
  TaskListResponse,
  TaskSLA,
  TaskStats,
  TaskStatsBucket,
  TaskStatsDivision,
  TaskStatsFilters,
  TaskStatsType,
  TaskStatusChange,
  TaskSummary,
  TaskRateResult,
  TaskTimelineEntry,
  TaskComment,
  TaskOwnerLink,
  UpdateTaskRequest,
  AssignTaskRequest,
  UnassignTaskRequest,
  TaskAssignmentHistoryEntry,
  TaskTypeSummary,
  TaskTypeSettingsPayload,
  TaskTypeGroupCatalogItem,
  TaskTypeGroupSettingsPayload,
  TaskTypicalCommentCatalogItem,
  TaskTypicalCommentSettingsPayload,
  TaskCatalogStateItem,
  TaskCatalogStateSettingsPayload,
  TaskTypeSequence,
  TaskTypeSequencePayload,
  BulkTaskActionRequest,
  BulkTaskActionResponse,
} from '../types';
import type { EmployeeHistoryFilter, EmployeeHistoryFacets, EmployeeHistoryResponse } from '@/features/users/types';
import { api } from '@/services/api';
import { toArray } from '@shared/lib/toArray';

export const taskKeys = {
  all: ['tasks'] as const,
  list: (filters: TaskListFilters) => [...taskKeys.all, 'list', filters] as const,
  detail: (id: number) => [...taskKeys.all, 'detail', id] as const,
  timeline: (id: number) => [...taskKeys.all, 'timeline', id] as const,
  sla: (id: number) => [...taskKeys.all, 'sla', id] as const,
  attachments: (taskId: number) => [...taskKeys.all, 'attachments', taskId] as const,
  additionalFields: (taskId: number) => [...taskKeys.all, 'additionalFields', taskId] as const,
  comments: (taskId: number) => [...taskKeys.all, 'comments', taskId] as const,
  commentAttachments: (taskId: number, commentId: number) =>
    [...taskKeys.all, 'attachments', taskId, 'comment', commentId] as const,
  stats: ['tasks', 'stats'] as const,
  exportJobs: () => [...taskKeys.all, 'exportJobs'] as const,
  typeCatalog: () => [...taskKeys.all, 'catalog', 'types'] as const,
  typeGroups: () => [...taskKeys.all, 'catalog', 'type-groups'] as const,
  statusCatalog: () => [...taskKeys.all, 'catalog', 'statuses'] as const,
  exportJob: (id: string) => [...taskKeys.all, 'exportJobs', id] as const,
};

const arrayParam = (value?: number[]) =>
  value && value.length > 0 ? value.join(',') : undefined;

const stringArrayParam = (value?: string[]) =>
  value && value.length > 0 ? value.join(',') : undefined;

const booleanParam = (value?: boolean) =>
  typeof value === 'boolean' ? String(value) : undefined;

export const createDefaultTaskStats = (): TaskStats => ({
  mine: { total: 0, inProgress: 0, overdue: 0 },
  all: { total: 0, inProgress: 0, overdue: 0 },
  byDivision: [],
  byType: [],
});

const normalizeBucket = (bucket?: Partial<TaskStatsBucket> | null): TaskStatsBucket => ({
  total: Number(bucket?.total ?? 0),
  inProgress: Number(bucket?.inProgress ?? 0),
  overdue: Number(bucket?.overdue ?? 0),
});

const normalizeDivisionStats = (
  items?: Array<Partial<TaskStatsDivision> | null> | null,
): TaskStatsDivision[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.reduce<TaskStatsDivision[]>((acc, item) => {
    if (!item) {
      return acc;
    }

    const divisionId = Number(item.divisionId ?? NaN);
    if (!Number.isFinite(divisionId)) {
      return acc;
    }

    const rawName = item.name;
    const normalizedName =
      rawName === undefined ? undefined : rawName === null ? null : String(rawName);

    const entry: TaskStatsDivision = {
      divisionId,
      inProgress: Number(item.inProgress ?? 0),
      overdue: Number(item.overdue ?? 0),
    };

    if (normalizedName !== undefined) {
      entry.name = normalizedName;
    }

    acc.push(entry);
    return acc;
  }, []);
};

const normalizeTypeStats = (
  items?: Array<Partial<TaskStatsType> | null> | null,
): TaskStatsType[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.reduce<TaskStatsType[]>((acc, item) => {
    if (!item) {
      return acc;
    }

    const taskTypeId = Number(item.taskTypeId ?? NaN);
    if (!Number.isFinite(taskTypeId)) {
      return acc;
    }

    const rawName = item.name;
    const normalizedName =
      rawName === undefined ? undefined : rawName === null ? null : String(rawName);

    const entry: TaskStatsType = {
      taskTypeId,
      inProgress: Number(item.inProgress ?? 0),
      overdue: Number(item.overdue ?? 0),
    };

    if (normalizedName !== undefined) {
      entry.name = normalizedName;
    }

    acc.push(entry);
    return acc;
  }, []);
};

type TaskStatsResponse = {
  data?: {
    mine?: Partial<TaskStatsBucket> | null;
    all?: Partial<TaskStatsBucket> | null;
    byDivision?: Array<Partial<TaskStatsDivision> | null> | null;
    byType?: Array<Partial<TaskStatsType> | null> | null;
  } | null;
};

const normalizeTaskStats = (raw?: TaskStatsResponse['data']): TaskStats => ({
  mine: normalizeBucket(raw?.mine),
  all: normalizeBucket(raw?.all),
  byDivision: normalizeDivisionStats(raw?.byDivision),
  byType: normalizeTypeStats(raw?.byType),
});

export async function fetchTasks(filters: TaskListFilters = {}): Promise<TaskListResponse> {
  const params = {
    page: filters.page,
    limit: filters.limit,
    search: filters.search,
    taskTypeId: arrayParam(filters.taskTypeId),
    statusId: arrayParam(filters.statusId),
    employeeId: arrayParam(filters.employeeId),
    divisionId: arrayParam(filters.divisionId),
    customerId: arrayParam(filters.customerId),
    deviceId: arrayParam(filters.deviceId),
    nodeId: arrayParam(filters.nodeId),
    cableLineId: arrayParam(filters.cableLineId),
    watcherEmployeeId: arrayParam(filters.watcherEmployeeId),
    watcherDivisionId: arrayParam(filters.watcherDivisionId),
    tagId: arrayParam(filters.tagId),
    dateAddFrom: filters.dateAddFrom,
    dateAddTo: filters.dateAddTo,
    dateWorkFrom: filters.dateWorkFrom,
    dateWorkTo: filters.dateWorkTo,
    dateUpdateFrom: filters.dateUpdateFrom,
    dateUpdateTo: filters.dateUpdateTo,
    dateFinishFrom: filters.dateFinishFrom,
    dateFinishTo: filters.dateFinishTo,
    onlyOverdue: booleanParam(filters.onlyOverdue),
    order: filters.order,
  };

  const { data } = await api.get<TaskListResponse>('/tasks', {
    params,
  });

  return data;
}

export async function createTask(payload: CreateTaskRequest): Promise<CreateTaskResponse['item']> {
  const { data } = await api.post<CreateTaskResponse>('/tasks', payload);
  return data.item;
}

export async function listTaskComments(taskId: number): Promise<TaskComment[]> {
  const { data } = await api.get<{ items: TaskComment[]; total: number }>(`/tasks/${taskId}/comments`);
  return toArray<TaskComment>(data.items);
}

export async function createTaskComment(
  taskId: number,
  payload: CreateTaskCommentRequest,
): Promise<CreateTaskCommentResponse['item']> {
  const { data } = await api.post<CreateTaskCommentResponse>(`/tasks/${taskId}/comments`, payload);
  return data.item;
}

export async function updateTaskComment(
  taskId: number,
  commentId: number,
  payload: UpdateTaskCommentRequest,
): Promise<UpdateTaskCommentResponse['item']> {
  const { data } = await api.put<UpdateTaskCommentResponse>(`/tasks/${taskId}/comments/${commentId}`, payload);
  return data.item;
}

export async function setTaskRate(taskId: number, rate: number): Promise<TaskRateResult> {
  const { data } = await api.put<{ item: TaskRateResult }>(`/tasks/${taskId}/rate`, { rate });
  return data.item;
}

export async function checkTaskVerifyCode(taskId: number, verifyCode: string): Promise<void> {
  await api.post<{ result: 'OK' }>(`/tasks/${taskId}/verify-code`, { verifyCode });
}

export async function fetchTaskDetails(taskId: number): Promise<TaskDetails> {
  const { data } = await api.get<TaskDetails>(`/tasks/${taskId}`);
  return data;
}

export interface TaskDirectCommutationItem {
  id: number;
  taskPort?: number;
  peerDeviceId: number;
  peerDeviceName: string;
  peerPort?: number;
  peerFastLinks?: Array<{ label: string; url: string }>;
}

export interface TaskDirectCommutationsResponse {
  items: TaskDirectCommutationItem[];
  total: number;
  page: number;
  limit: number;
}

export async function fetchTaskDirectCommutations(taskId: number, page = 1): Promise<TaskDirectCommutationsResponse> {
  const { data } = await api.get<TaskDirectCommutationsResponse>(`/tasks/${taskId}/commutations/direct`, {
    params: { page, limit: 20 },
  });
  return data;
}

export interface TaskDirectCrossItem {
  id: number;
  taskPort?: number;
  crossId: string;
  crossName: string;
  crossSide?: number;
  crossPort?: number;
}

export interface TaskDirectCrossesResponse {
  items: TaskDirectCrossItem[];
  total: number;
  page: number;
  limit: number;
}

export async function fetchTaskDirectCrosses(taskId: number, page = 1): Promise<TaskDirectCrossesResponse> {
  const { data } = await api.get<TaskDirectCrossesResponse>(`/tasks/${taskId}/commutations/crosses/direct`, {
    params: { page, limit: 20 },
  });
  return data;
}

export async function fetchTaskTimeline(taskId: number): Promise<TaskTimelineEntry[]> {
  const { data } = await api.get<{ items: TaskTimelineEntry[] }>(`/tasks/${taskId}/timeline`);
  return toArray<TaskTimelineEntry>(data.items);
}

export async function fetchTaskSLA(taskId: number): Promise<TaskSLA> {
  const { data } = await api.get<TaskSLA>(`/tasks/${taskId}/sla`);
  return data;
}

export async function listTaskAttachments(taskId: number): Promise<TaskAttachment[]> {
  const { data } = await api.get<{ items: TaskAttachment[] }>(`/tasks/${taskId}/attachments`);
  return toArray<TaskAttachment>(data.items);
}

export async function uploadTaskAttachment(
  taskId: number,
  payload: { file: File | Blob; comment?: string },
): Promise<TaskAttachment> {
  const formData = new FormData();
  const filename = payload.file instanceof File ? payload.file.name : 'attachment';
  formData.append('file', payload.file, filename);
  if (payload.comment) {
    formData.append('comment', payload.comment);
  }

  const { data } = await api.post<{ item: TaskAttachment }>(`/tasks/${taskId}/attachments`, formData);
  return data.item;
}

export async function deleteTaskAttachment(taskId: number, uuid: string): Promise<TaskAttachment> {
  const { data } = await api.delete<{ item: TaskAttachment }>(`/tasks/${taskId}/attachments/${uuid}`);
  return data.item;
}

export async function listTaskCommentAttachments(
  taskId: number,
  commentId: number,
): Promise<TaskAttachment[]> {
  const { data } = await api.get<{ items: TaskAttachment[] }>(
    `/tasks/${taskId}/comments/${commentId}/attachments`,
  );
  return data.items;
}

export async function uploadTaskCommentAttachment(
  taskId: number,
  commentId: number,
  payload: { file: File | Blob; comment?: string },
): Promise<TaskAttachment> {
  const formData = new FormData();
  const filename = payload.file instanceof File ? payload.file.name : 'attachment';
  formData.append('file', payload.file, filename);
  if (payload.comment) {
    formData.append('comment', payload.comment);
  }

  const { data } = await api.post<{ item: TaskAttachment }>(
    `/tasks/${taskId}/comments/${commentId}/attachments`,
    formData,
  );
  return data.item;
}

export async function deleteTaskCommentAttachment(
  taskId: number,
  commentId: number,
  uuid: string,
): Promise<TaskAttachment> {
  const { data } = await api.delete<{ item: TaskAttachment }>(
    `/tasks/${taskId}/comments/${commentId}/attachments/${uuid}`,
  );
  return data.item;
}

export async function verifyTaskDeadline(
  payload: DeadlineVerificationRequest,
): Promise<DeadlineVerificationResponse> {
  const { taskId, ...body } = payload;
  const endpoint =
    typeof taskId === 'number' && taskId > 0
      ? `/tasks/${taskId}/deadline/verify`
      : '/tasks/deadline/verify';
  const { data } = await api.post<DeadlineVerificationResponse>(endpoint, {
    ...(taskId ? { taskId } : {}),
    ...body,
  });
  return data;
}

export async function fetchTaskStats(filters: TaskStatsFilters = {}): Promise<TaskStats> {
  const params = {
    employeeId: arrayParam(filters.employeeId),
    divisionId: arrayParam(filters.divisionId),
    taskTypeId: arrayParam(filters.taskTypeId),
    statusRole: stringArrayParam(filters.statusRole),
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  };

  const { data } = await api.get<TaskStatsResponse>('/tasks/stats', {
    params,
  });

  if (!data) {
    return createDefaultTaskStats();
  }

  return normalizeTaskStats(data.data);
}

export interface TaskDivisionOption {
  divisionId: number;
  divisionName?: string | null;
}

export async function searchTaskDivisions(query: string, limit = 10): Promise<TaskDivisionOption[]> {
  const { data } = await api.get<{ data: TaskDivisionOption[] }>('/tasks/filters/divisions', {
    params: {
      q: query || undefined,
      limit,
    },
  });
  return toArray<TaskDivisionOption>(data.data);
}

export interface TaskTagItem {
  id: number;
  name: string;
}

export interface TaskDeviceLookupItem {
  id: number;
  name: string;
  hostname: string | null;
  location: string | null;
}

type TaskDeviceLookupDto = {
  id?: number | string | null;
  name?: string | null;
  hostname?: string | null;
  location?: string | null;
};

type TaskDeviceLookupResponse = {
  items?: TaskDeviceLookupDto[] | null;
  data?: TaskDeviceLookupDto[] | null;
};

type TaskTagDto = {
  id?: number | string | null;
  name?: string | null;
};

type TaskTagResponse = {
  data?: TaskTagDto[] | null;
  items?: TaskTagDto[] | null;
};

const normalizeTaskDevices = (items?: TaskDeviceLookupDto[] | null): TaskDeviceLookupItem[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      if (!item) {
        return null;
      }

      const id = Number(item.id ?? NaN);
      if (!Number.isFinite(id)) {
        return null;
      }

      return {
        id,
        name: typeof item.name === 'string' ? item.name : '',
        hostname: item.hostname ?? null,
        location: item.location ?? null,
      } satisfies TaskDeviceLookupItem;
    })
    .filter((item): item is TaskDeviceLookupItem => item !== null);
};

const normalizeTaskTags = (items?: TaskTagDto[] | null): TaskTagItem[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      if (!item) {
        return null;
      }

      const id = Number(item.id ?? NaN);
      if (!Number.isFinite(id)) {
        return null;
      }

      const rawName = item.name;
      const name = typeof rawName === 'string' ? rawName.trim() : '';
      if (name.length === 0) {
        return null;
      }

      return {
        id,
        name,
      } satisfies TaskTagItem;
    })
    .filter((item): item is TaskTagItem => item !== null);
};

// Печать бланка наряда (эталон — TaskHtml::doc:3612, DocumentAR::getBodyDocument).

export interface TaskDocumentTemplate {
  id: number;
  name: string;
  isMain: boolean;
}

export async function getTaskDocumentTemplates(
  taskId: number
): Promise<{ items: TaskDocumentTemplate[] }> {
  const { data } = await api.get<{ items: TaskDocumentTemplate[] }>(
    `/tasks/${taskId}/document-templates`
  );
  return { items: data?.items ?? [] };
}

/**
 * Ответ — JSON с готовым HTML, а не страница по ссылке: токен лежит в localStorage,
 * и GET в новой вкладке ушёл бы без заголовка Authorization.
 */
export async function renderTaskDocument(
  taskId: number,
  templateId: number
): Promise<{ html: string }> {
  const { data } = await api.get<{ html: string }>(
    `/tasks/${taskId}/document-templates/${templateId}/render`
  );
  return data;
}

export async function searchTaskDevices(query: string, limit = 10): Promise<TaskDeviceLookupItem[]> {
  const { data } = await api.get<TaskDeviceLookupResponse>('/tasks/filters/devices', {
    params: { query, limit },
  });

  return normalizeTaskDevices(data?.items ?? data?.data ?? []);
}

export async function fetchAllTags(): Promise<TaskTagItem[]> {
  const { data } = await api.get<TaskTagResponse>('/tasks/filters/marks');

  if (!data) {
    return [];
  }

  const candidates = data.data ?? data.items ?? [];
  return normalizeTaskTags(candidates);
}

export async function exportTasks(
  filters: TaskListFilters,
  format: 'csv' | 'pdf' = 'csv',
): Promise<Blob> {
  const params = {
    page: filters.page,
    limit: filters.limit,
    search: filters.search,
    order: filters.order,
    format,
    taskTypeId: arrayParam(filters.taskTypeId),
    statusId: arrayParam(filters.statusId),
    employeeId: arrayParam(filters.employeeId),
    divisionId: arrayParam(filters.divisionId),
    watcherEmployeeId: arrayParam(filters.watcherEmployeeId),
    watcherDivisionId: arrayParam(filters.watcherDivisionId),
    customerId: arrayParam(filters.customerId),
    deviceId: arrayParam(filters.deviceId),
    nodeId: arrayParam(filters.nodeId),
    cableLineId: arrayParam(filters.cableLineId),
    tagId: arrayParam(filters.tagId),
    dateAddFrom: filters.dateAddFrom,
    dateAddTo: filters.dateAddTo,
    dateWorkFrom: filters.dateWorkFrom,
    dateWorkTo: filters.dateWorkTo,
    dateUpdateFrom: filters.dateUpdateFrom,
    dateUpdateTo: filters.dateUpdateTo,
    dateFinishFrom: filters.dateFinishFrom,
    dateFinishTo: filters.dateFinishTo,
    onlyOverdue: booleanParam(filters.onlyOverdue),
  };

  const response = await api.get<Blob>('/tasks/export', {
    params,
    responseType: 'blob',
  });

  return response.data;
}

export type TaskExportJobStatus = 'queued' | 'processing' | 'completed' | 'failed';

export interface TaskExportJobSummary {
  id: string;
  status: TaskExportJobStatus;
  format: 'csv' | 'pdf';
  error?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TaskExportJobDetail extends TaskExportJobSummary {
  filters: TaskListFilters;
  result?: {
    filename: string;
    contentType: string;
    size: number;
  };
}

const serializeFilters = (filters: TaskListFilters) => ({
  page: filters.page,
  limit: filters.limit,
  taskTypeId: filters.taskTypeId,
  statusId: filters.statusId,
  employeeId: filters.employeeId,
  divisionId: filters.divisionId,
  watcherDivisionId: filters.watcherDivisionId,
  watcherEmployeeId: filters.watcherEmployeeId,
  customerId: filters.customerId,
  deviceId: filters.deviceId,
  nodeId: filters.nodeId,
  cableLineId: filters.cableLineId,
  tagId: filters.tagId,
  search: filters.search,
  order: filters.order,
  dateAddFrom: filters.dateAddFrom,
  dateAddTo: filters.dateAddTo,
  dateWorkFrom: filters.dateWorkFrom,
  dateWorkTo: filters.dateWorkTo,
  dateUpdateFrom: filters.dateUpdateFrom,
  dateUpdateTo: filters.dateUpdateTo,
  dateFinishFrom: filters.dateFinishFrom,
  dateFinishTo: filters.dateFinishTo,
  onlyOverdue: filters.onlyOverdue,
});

const mapJobSummary = (value: TaskExportJobSummary): TaskExportJobSummary => ({
  ...value,
  error: value.error ?? null,
});

const mapJobDetail = (value: TaskExportJobDetail): TaskExportJobDetail => ({
  ...mapJobSummary(value),
  filters: value.filters,
  result: value.result,
});

export async function queueTaskExportJob(
  format: 'csv' | 'pdf',
  filters: TaskListFilters,
): Promise<TaskExportJobSummary> {
  const payload = {
    format,
    filters: serializeFilters(filters),
  };

  const { data } = await api.post<{ data: TaskExportJobSummary }>('/tasks/export/jobs', payload);
  return mapJobSummary(data.data);
}

export async function listTaskExportJobs(params?: {
  status?: TaskExportJobStatus[];
  limit?: number;
  offset?: number;
}): Promise<{ items: TaskExportJobSummary[]; total: number }> {
  const searchParams = new URLSearchParams();
  params?.status?.forEach((status) => searchParams.append('status', status));
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const query = searchParams.toString();
  const { data } = await api.get<{ data: { items: TaskExportJobSummary[]; total: number } }>(
    `/tasks/export/jobs${query ? `?${query}` : ''}`,
  );

  return {
    items: data.data.items.map(mapJobSummary),
    total: data.data.total,
  };
}

export async function getTaskExportJob(jobId: string): Promise<TaskExportJobDetail> {
  const { data } = await api.get<{ data: TaskExportJobDetail }>(`/tasks/export/jobs/${jobId}`);
  return mapJobDetail(data.data);
}

export async function downloadTaskExportJob(
  jobId: string,
): Promise<{ blob: Blob; filename: string; contentType: string }> {
  const response = await api.get<Blob>(`/tasks/export/jobs/${jobId}/download`, {
    responseType: 'blob',
  });

  const disposition = response.headers['content-disposition'] ?? '';
  const match = disposition.match(/filename=([^;]+)/i);
  const filename = match?.[1]?.replace(/"/g, '').trim() ?? `tasks-export-${jobId}`;
  const contentType = String(response.headers['content-type'] ?? 'application/octet-stream');

  return {
    blob: response.data,
    filename,
    contentType,
  };
}

export interface UpdateTaskStatusPayload {
  statusId: number;
  comment?: string;
}

export async function updateTaskStatus(
  taskId: number,
  payload: UpdateTaskStatusPayload,
): Promise<TaskStatusChange> {
  const { data } = await api.put<{ data: TaskStatusChange }>(`/tasks/${taskId}/status`, payload);
  return data.data;
}

export async function updateTask(taskId: number, payload: UpdateTaskRequest): Promise<TaskDetails> {
  const { data } = await api.put<{ item: TaskDetails }>(`/tasks/${taskId}`, payload);
  return data.item;
}

// F-ADDFIELDS-1: доп.поля задачи. saveTaskAdditionalFields шлёт ПОЛНЫЙ желаемый
// набор (ключ fieldId -> значение); поле без ключа считается неотправленным, а
// не «очистить» — сервер трактует так же (backend/.../additional_fields_handler.go).
// F-ADDFIELDS-1, шаг 3: полный каталог доп.полей категории task (не по типу задачи —
// v4-паритет, GroupActionHtml.php листает AdditionalFieldAR::getByCategory целиком)
// для формы выбора поля в bulk-действии additional_field_task_change.
export async function fetchTaskAdditionalFieldCatalog(): Promise<TaskAdditionalFieldCatalogItem[]> {
  const { data } = await api.get<{ items: TaskAdditionalFieldCatalogItem[] }>('/tasks/additional-fields/catalog');
  return toArray<TaskAdditionalFieldCatalogItem>(data.items);
}

export async function fetchTaskAdditionalFields(taskId: number): Promise<TaskAdditionalFieldItem[]> {
  const { data } = await api.get<{ items: TaskAdditionalFieldItem[] }>(`/tasks/${taskId}/additional-fields`);
  return toArray<TaskAdditionalFieldItem>(data.items);
}

export async function saveTaskAdditionalFields(
  taskId: number,
  values: Record<number, string>,
): Promise<TaskAdditionalFieldItem[]> {
  const payload = { values: Object.fromEntries(Object.entries(values).map(([id, value]) => [String(id), value])) };
  const { data } = await api.put<{ items: TaskAdditionalFieldItem[] }>(`/tasks/${taskId}/additional-fields`, payload);
  return data.items;
}

export async function deleteTask(taskId: number): Promise<void> {
  await api.delete(`/tasks/${taskId}`);
}

export async function assignEmployee(taskId: number, payload: AssignTaskRequest): Promise<void> {
  await api.post(`/tasks/${taskId}/assign`, payload);
}

export async function bulkTasksAction(payload: BulkTaskActionRequest): Promise<BulkTaskActionResponse> {
  const { data } = await api.post<BulkTaskActionResponse>('/tasks/bulk', payload);
  return data;
}

export async function unassignEmployee(taskId: number, payload: UnassignTaskRequest): Promise<void> {
  await api.post(`/tasks/${taskId}/unassign`, payload);
}

export async function addTaskWatcher(taskId: number, employeeId: number): Promise<{ mutated: boolean }> {
  const { data } = await api.post<{ mutated: boolean }>(`/tasks/${taskId}/watchers`, { employeeId });
  return data;
}

export async function removeTaskWatcher(taskId: number, employeeId: number): Promise<{ mutated: boolean }> {
  const { data } = await api.delete<{ mutated: boolean }>(`/tasks/${taskId}/watchers/${employeeId}`);
  return data;
}

export async function addTaskWatcherDivision(taskId: number, divisionId: number): Promise<{ mutated: boolean }> {
  const { data } = await api.post<{ mutated: boolean }>(`/tasks/${taskId}/watcher-divisions`, { divisionId });
  return data;
}

export async function removeTaskWatcherDivision(taskId: number, divisionId: number): Promise<{ mutated: boolean }> {
  const { data } = await api.delete<{ mutated: boolean }>(`/tasks/${taskId}/watcher-divisions/${divisionId}`);
  return data;
}

export async function addTaskCustomer(taskId: number, customerId: number): Promise<{ mutated: boolean }> {
  const { data } = await api.post<{ mutated: boolean }>(`/tasks/${taskId}/customers`, { customerId });
  return data;
}

export async function removeTaskCustomer(taskId: number, customerId: number): Promise<{ mutated: boolean }> {
  const { data } = await api.delete<{ mutated: boolean }>(`/tasks/${taskId}/customers/${customerId}`);
  return data;
}

export async function searchTaskOwners(query: string, limit = 10): Promise<TaskOwnerLink[]> {
  const { data } = await api.get<{ items: TaskOwnerLink[] }>('/tasks/filters/owners', {
    params: {
      query,
      limit,
    },
  });
  return toArray<TaskOwnerLink>(data.items);
}

export async function addTaskOwner(taskId: number, ownerId: number): Promise<{ mutated: boolean }> {
  const { data } = await api.post<{ mutated: boolean }>(`/tasks/${taskId}/owners/${ownerId}`);
  return data;
}

export async function removeTaskOwner(taskId: number, ownerId: number): Promise<{ mutated: boolean }> {
  const { data } = await api.delete<{ mutated: boolean }>(`/tasks/${taskId}/owners/${ownerId}`);
  return data;
}

export async function addTaskDevice(taskId: number, deviceId: number): Promise<{ mutated: boolean }> {
  const { data } = await api.post<{ mutated: boolean }>(`/tasks/${taskId}/devices/${deviceId}`);
  return data;
}

export async function removeTaskDevice(taskId: number, deviceId: number): Promise<{ mutated: boolean }> {
  const { data } = await api.delete<{ mutated: boolean }>(`/tasks/${taskId}/devices/${deviceId}`);
  return data;
}

export async function addTaskMark(taskId: number, tagId: number): Promise<{ mutated: boolean }> {
  const { data } = await api.post<{ mutated: boolean }>(`/tasks/${taskId}/marks`, { tagId });
  return data;
}

export async function removeTaskMark(taskId: number, tagId: number): Promise<{ mutated: boolean }> {
  const { data } = await api.delete<{ mutated: boolean }>(`/tasks/${taskId}/marks`, { data: { tagId } });
  return data;
}

export async function getAssignmentHistory(taskId: number): Promise<TaskAssignmentHistoryEntry[]> {
  const { data } = await api.get<{ data: TaskAssignmentHistoryEntry[] }>(`/tasks/${taskId}/assignments`);
  return toArray<TaskAssignmentHistoryEntry>(data.data);
}

// Журнал задачи (erp.employees_actions_log, коды из TaskAR::getTypeChangeTyperList).
// Тип действия, автор и поиск по комментарию — СЕРВЕРНЫЕ фильтры, как у истории устройства:
// иначе число под таблицей относится к загруженной странице, а не ко всему набору.
export async function getTaskHistory(
  taskId: number,
  filter: EmployeeHistoryFilter,
): Promise<EmployeeHistoryResponse> {
  const params: Record<string, string | number> = {
    date_from: filter.date_from,
    date_to: filter.date_to,
  };
  if (filter.type_id) params.type_id = filter.type_id;
  if (filter.employee_id) params.employee_id = filter.employee_id;
  if (filter.search) params.search = filter.search;
  if (filter.page) params.page = filter.page;
  if (filter.limit) params.limit = filter.limit;
  const { data } = await api.get<EmployeeHistoryResponse>(`/tasks/${taskId}/operations-history`, { params });
  return data;
}

// Значения выпадашек журнала задачи — по всему периоду, а не по загруженной странице.
export async function getTaskHistoryFilters(
  taskId: number,
  period: { date_from: string; date_to: string },
): Promise<EmployeeHistoryFacets> {
  const { data } = await api.get<EmployeeHistoryFacets>(`/tasks/${taskId}/operations-history/filters`, {
    params: { date_from: period.date_from, date_to: period.date_to },
  });
  return data;
}

// Флаг настройки TASK_IS_ALLOW_DELETE: разрешено ли удаление задач (v4-паритет).
// Скрытие кнопки — косметика: сервер отказывает в DELETE /tasks/:id независимо от неё.
export async function fetchTaskDeleteAllowed(): Promise<boolean> {
  const { data } = await api.get<{ allowDelete: boolean }>('/tasks/settings/allow-delete');
  return data.allowDelete === true;
}

// Получить список всех типов задач (для форм периодических расписаний)
export async function fetchTaskTypes(): Promise<TaskTypeSummary[]> {
  const { data } = await api.get<{ items: TaskTypeSummary[] }>('/tasks/types');
  return toArray<TaskTypeSummary>(data.items);
}

const mapRecordValues = <T>(value: Record<string, T> | T[] | undefined | null): T[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : Object.values(value);
};

export async function fetchTaskTypeGroups(): Promise<TaskTypeGroupCatalogItem[]> {
  const { data } = await api.get<{ data: Record<string, TaskTypeGroupCatalogItem> }>('/tasks/type-groups');
  return mapRecordValues(data.data).sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
}

export async function createTaskTypeGroup(payload: TaskTypeGroupSettingsPayload): Promise<TaskTypeGroupCatalogItem> {
  const { data } = await api.post<{ item: TaskTypeGroupCatalogItem }>('/tasks/settings/type-groups', payload);
  return data.item;
}

export async function updateTaskTypeGroup(id: number, payload: TaskTypeGroupSettingsPayload): Promise<TaskTypeGroupCatalogItem> {
  const { data } = await api.put<{ item: TaskTypeGroupCatalogItem }>(`/tasks/settings/type-groups/${id}`, payload);
  return data.item;
}

export async function deleteTaskTypeGroup(id: number): Promise<void> {
  await api.delete(`/tasks/settings/type-groups/${id}`);
}

export async function fetchTaskTypicalComments(): Promise<TaskTypicalCommentCatalogItem[]> {
  const { data } = await api.get<{ data: Record<string, TaskTypicalCommentCatalogItem> }>('/tasks/typical-comments');
  return mapRecordValues(data.data).sort((a, b) => a.id - b.id);
}

export async function createTaskTypicalComment(payload: TaskTypicalCommentSettingsPayload): Promise<TaskTypicalCommentCatalogItem> {
  const { data } = await api.post<{ item: TaskTypicalCommentCatalogItem }>('/tasks/settings/typical-comments', payload);
  return data.item;
}

export async function updateTaskTypicalComment(id: number, payload: TaskTypicalCommentSettingsPayload): Promise<TaskTypicalCommentCatalogItem> {
  const { data } = await api.put<{ item: TaskTypicalCommentCatalogItem }>(`/tasks/settings/typical-comments/${id}`, payload);
  return data.item;
}

export async function deleteTaskTypicalComment(id: number): Promise<void> {
  await api.delete(`/tasks/settings/typical-comments/${id}`);
}

export async function fetchTaskCatalogStates(taskTypeId?: number): Promise<TaskCatalogStateItem[]> {
  const { data } = await api.get<{ data: Record<string, TaskCatalogStateItem> }>('/tasks/catalog-state', {
    params: taskTypeId ? { type_id: taskTypeId } : undefined,
  });
  return mapRecordValues(data.data).sort((a, b) => a.id - b.id);
}

export async function createTaskCatalogState(payload: TaskCatalogStateSettingsPayload): Promise<TaskCatalogStateItem> {
  const { data } = await api.post<{ item: TaskCatalogStateItem }>('/tasks/settings/catalog-states', payload);
  return data.item;
}

export async function updateTaskCatalogState(id: number, payload: TaskCatalogStateSettingsPayload): Promise<TaskCatalogStateItem> {
  const { data } = await api.put<{ item: TaskCatalogStateItem }>(`/tasks/settings/catalog-states/${id}`, payload);
  return data.item;
}

export async function deleteTaskCatalogState(id: number): Promise<void> {
  await api.delete(`/tasks/settings/catalog-states/${id}`);
}

export async function createTaskTypeSettings(payload: TaskTypeSettingsPayload): Promise<TaskTypeSummary> {
  const { data } = await api.post<{ item: TaskTypeSummary }>('/tasks/settings/types', payload);
  return data.item;
}

export async function updateTaskTypeSettings(id: number, payload: TaskTypeSettingsPayload): Promise<TaskTypeSummary> {
  const { data } = await api.put<{ item: TaskTypeSummary }>(`/tasks/settings/types/${id}`, payload);
  return data.item;
}

export async function deleteTaskTypeSettings(id: number): Promise<void> {
  await api.delete(`/tasks/settings/types/${id}`);
}

export async function fetchTaskTypeSequences(taskTypeId: number): Promise<TaskTypeSequence[]> {
  const { data } = await api.get<{ items: TaskTypeSequence[] }>('/tasks/settings/sequences', {
    params: { taskTypeId },
  });
  return toArray<TaskTypeSequence>(data.items);
}

export async function createTaskTypeSequence(payload: TaskTypeSequencePayload): Promise<TaskTypeSequence> {
  const { data } = await api.post<{ item: TaskTypeSequence }>('/tasks/settings/sequences', payload);
  return data.item;
}

export async function updateTaskTypeSequence(id: number, payload: TaskTypeSequencePayload): Promise<TaskTypeSequence> {
  const { data } = await api.put<{ item: TaskTypeSequence }>(`/tasks/settings/sequences/${id}`, payload);
  return data.item;
}

export async function deleteTaskTypeSequence(id: number): Promise<void> {
  await api.delete(`/tasks/settings/sequences/${id}`);
}

// Re-export all functions for backward compatibility
export const tasksApi = {
  fetchTasks,
  fetchTaskDetails,
  fetchTaskTimeline,
  fetchTaskSLA,
  listTaskAttachments,
  uploadTaskAttachment,
  deleteTaskAttachment,
  fetchTaskAdditionalFields,
  saveTaskAdditionalFields,
  fetchTaskAdditionalFieldCatalog,
  listTaskCommentAttachments,
  uploadTaskCommentAttachment,
  deleteTaskCommentAttachment,
  fetchTaskStats,
  searchTaskDivisions,
  fetchAllTags,
  exportTasks,
  queueTaskExportJob,
  listTaskExportJobs,
  getTaskExportJob,
  downloadTaskExportJob,
  createTask,
  updateTask,
  deleteTask,
  listTaskComments,
  createTaskComment,
  updateTaskComment,
  setTaskRate,
  checkTaskVerifyCode,
  updateTaskStatus,
  verifyTaskDeadline,
  assignEmployee,
  bulkTasksAction,
  unassignEmployee,
  getAssignmentHistory,
  fetchTaskTypes,
  createTaskTypeSettings,
  updateTaskTypeSettings,
  deleteTaskTypeSettings,
  fetchTaskTypeGroups,
  createTaskTypeGroup,
  updateTaskTypeGroup,
  deleteTaskTypeGroup,
  fetchTaskTypicalComments,
  createTaskTypicalComment,
  updateTaskTypicalComment,
  deleteTaskTypicalComment,
  fetchTaskCatalogStates,
  createTaskCatalogState,
  updateTaskCatalogState,
  deleteTaskCatalogState,
  fetchTaskTypeSequences,
  createTaskTypeSequence,
  updateTaskTypeSequence,
  deleteTaskTypeSequence,
  addTaskWatcher,
  removeTaskWatcher,
  addTaskWatcherDivision,
  removeTaskWatcherDivision,
  addTaskCustomer,
  removeTaskCustomer,
  searchTaskOwners,
  addTaskOwner,
  removeTaskOwner,
  addTaskDevice,
  removeTaskDevice,
  addTaskMark,
  removeTaskMark,
  searchTaskDevices,
  getTaskDocumentTemplates,
  renderTaskDocument,
};
